import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SigningPackageView } from "@wms/domain";
import { Alert, App, Card, Spin, Typography } from "antd";
import { useParams } from "react-router-dom";
import { portalApi } from "../api/portal";
import { errorMessage } from "../api/client";
import { SigningView, useProviders } from "./SigningView";

/** C05 / 13.3: the limited link — one package, one version; viewing is not signing; no cabinet session is created. */
export function SignLinkPage() {
  const { token = "" } = useParams();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: ["sign-link", token], queryFn: () => portalApi.public<SigningPackageView>(`/signing/link/${token}`), retry: false });
  const providers = useProviders(() => portalApi.public(`/signing/link/${token}/providers`), !!view.data);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["sign-link", token] });
  const sign = useMutation({
    mutationFn: (body: Record<string, unknown>) => portalApi.public<{ status: string; package_status: string }>(`/signing/link/${token}/sign`, body),
    onSuccess: (r) => {
      message.success(r.status === "signed" ? "Документ подписан" : "Подпись принята, результат проверки пока неизвестен");
      refresh();
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const reject = useMutation({
    mutationFn: ({ reason, expected_hash }: { reason: string; expected_hash: string }) =>
      portalApi.public(`/signing/link/${token}/reject`, { reason, expected_hash }),
    onSuccess: () => {
      message.success("Отказ зафиксирован");
      refresh();
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  return (
    <div style={{ minHeight: "100vh", padding: 16, boxSizing: "border-box", background: "var(--wms-canvas)" }}>
      <Card style={{ maxWidth: 960, margin: "0 auto" }} title="Подписание документа по ссылке">
        {view.isLoading && <Spin />}
        {view.isError && (
          <Alert
            type="warning"
            showIcon
            message="Ссылка недоступна"
            description={`${errorMessage(view.error)}. Запросите у склада новое приглашение.`}
          />
        )}
        {view.data && (
          <>
            <Typography.Paragraph type="secondary">
              Просмотр не является подписью. Подписывается именно эта версия документа; при любом изменении состава склад создаёт новую версию и новое приглашение.
            </Typography.Paragraph>
            <SigningView
              view={view.data}
              providers={providers.data ?? []}
              canSign
              canReject
              busy={sign.isPending || reject.isPending}
              onSign={(body) => sign.mutate(body)}
              onReject={(reason, expected_hash) => reject.mutate({ reason, expected_hash })}
            />
          </>
        )}
      </Card>
    </div>
  );
}
